import React, { useEffect, useState } from "react";
import Layout from "../../layouts/default";
import {
    Button,
    Spinner,
    Table,
    Row,
    Col,
    Form,
    Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import SmartPagination from "../Report/EmailCampaignRunReport/DynamicSmartPagination";
import {
    fetchIPAddressesData,
    createIPAddressData,
    updateIPAddressData,
} from "../../store/slices/ipManagement/ipSlice";
import { toast } from "react-toastify";
import { hasPermission } from "../../services/utils";
import { PERMISSIONS } from "../../contants/permission";

function IpManagement() {
    const dispatch = useDispatch();
    const { list, pagination, loading } = useSelector(
        (state) => state.ipManagementReducer
    );

    const limit = 10;

    const [filters, setFilters] = useState({
        ip_address: "",
        type: "",
    });

    const [appliedFilters, setAppliedFilters] = useState({});
    const [page, setPage] = useState(1);

    const totalPages = pagination?.totalPages || 0;

    const [showCreate, setShowCreate] = useState(false);
    const [newIP, setNewIP] = useState({
        ip_address: "",
        type: "ALLOW",
    });

    const [showEdit, setShowEdit] = useState(false);
    const [editIP, setEditIP] = useState({
        id: "",
        ip_address: "",
        type: "",
    });

    useEffect(() => {
        loadIPs();
    }, [appliedFilters, page]);

    const loadIPs = async () => {
        await dispatch(
            fetchIPAddressesData({
                limit,
                page,
                filters: appliedFilters,
            })
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value.trim() }));
    };

    const applyFilters = () => {
        setAppliedFilters(filters);
        setPage(1);
    };

    const resetFilters = () => {
        const empty = { ip_address: "", type: "" };
        setFilters(empty);
        setAppliedFilters({});
        setPage(1);
    };

    const handleCreateIP = async (e) => {
        e.preventDefault();

        try {
            const userId = JSON.parse(localStorage.getItem("user"))?.data?.id;

            if (!newIP.ip_address?.trim()) {
                toast.error("IP Address is required");
                return;
            }

            const payload = {
                ip_address: newIP.ip_address.trim(),
                type: newIP.type,
                createdBy: userId,
                updatedBy: userId,
            };

            const res = await dispatch(createIPAddressData(payload)).unwrap();

            console.log("res : ", res)

            if (!res?.success) {
                toast.error(res?.message || "Failed to create IP");
                return;
            }

            toast.success("IP created successfully");
            setShowCreate(false);
            setNewIP({ ip_address: "", type: "ALLOW" });
            loadIPs();

        } catch (err) {
            console.error("Create IP Error:", err);
            if (typeof err === 'string') {
                if (err.includes('Duplicate')) {
                    toast.error("IP Already Present in our record.");
                }
                else {
                    toast.error(err);
                }
            }
            else if (err?.message) {
                toast.error(err.message);
            } else {
                toast.error("Something went wrong while creating IP");
            }
        }
    };


    const openEditModal = (ip) => {
        setEditIP({
            id: ip.id,
            ip_address: ip.ip_address,
            type: ip.type,
        });
        setShowEdit(true);
    };

    const handleUpdateIP = async (e) => {
        e.preventDefault();

        try {
            const userId = JSON.parse(localStorage.getItem("user"))?.data?.id;

            if (!editIP.ip_address?.trim()) {
                toast.error("IP Address is required");
                return;
            }

            const payload = {
                id: editIP.id,
                data: {
                    ip_address: editIP.ip_address.trim(),
                    type: editIP.type,
                    updatedBy: userId,
                },
            };

            const res = await dispatch(updateIPAddressData(payload)).unwrap();

            if (!res?.success) {
                toast.error(res?.message || "Failed to update IP");
                return;
            }

            toast.success("IP updated successfully");
            setShowEdit(false);
            loadIPs();

        } catch (err) {
            console.error("Update IP Error:", err);

            if (typeof err === 'string') {
                if (err.includes('Duplicate')) {
                    toast.error("IP Already Present in our record.");
                }
                else {
                    toast.error(err);
                }
            }
            else if (err?.message) {
                toast.error(err.message);
            } else {
                toast.error("Something went wrong while updating IP");
            }
        }
    };


    return (
        <Layout>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center my-3 px-3">
                <h4 className="fw-semibold mb-0">IP Management</h4>
                {
                    hasPermission(PERMISSIONS.IP_MANAGEMENT.CREATE) && <Button variant="primary" onClick={() => setShowCreate(true)}>
                        + Add IP
                    </Button>
                }
            </div>

            {/* Filters */}
            <div className="p-3 border rounded bg-light mx-3 mb-3">
                <Row className="g-2 align-items-end">

                    {/* IP Address */}
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>IP Address</Form.Label>
                            <Form.Control
                                type="text"
                                name="ip_address"
                                placeholder="Search IP"
                                value={filters.ip_address}
                                onChange={handleFilterChange}
                            />
                        </Form.Group>
                    </Col>

                    {/* Type */}
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Type</Form.Label>
                            <Form.Select
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                            >
                                <option value="">All</option>
                                <option value="ALLOW">Allow</option>
                                <option value="BLOCK">Block</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col className="d-flex justify-content-end gap-2 ms-auto">
                        <Button className="px-4" onClick={applyFilters}>
                            Apply
                        </Button>
                        <Button variant="secondary" className="px-4" onClick={resetFilters}>
                            Reset
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Table */}
            <div className="mx-3">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                        <p>Loading...</p>
                    </div>
                ) : list?.length ? (
                    <>
                        <Table bordered hover>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>IP Address</th>
                                    <th>Type</th>
                                    <th>Created At</th>
                                    <th>Updated On</th>
                                    {
                                        hasPermission(PERMISSIONS.IP_MANAGEMENT.EDIT) && <th>Actions</th>
                                    }
                                </tr>
                            </thead>

                            <tbody>
                                {list.map((ip, index) => (
                                    <tr key={ip.id}>
                                        <td>{(page - 1) * limit + index + 1}</td>
                                        <td>{ip.ip_address}</td>
                                        <td>
                                            <span
                                                className={`badge ${ip.type === "ALLOW" ? "bg-success" : "bg-danger"
                                                    } px-3 py-1`}
                                            >
                                                {ip.type}
                                            </span>
                                        </td>
                                        <td>
                                            {ip.createdAt
                                                ? new Date(ip.createdAt).toLocaleString()
                                                : "NA"}
                                        </td>
                                        <td>
                                            {ip.updatedAt
                                                ? new Date(ip.updatedAt).toLocaleString()
                                                : "NA"}
                                        </td>
                                        {
                                            hasPermission(PERMISSIONS.IP_MANAGEMENT.EDIT) && <td>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => openEditModal(ip)}
                                                >
                                                    Edit
                                                </Button>
                                            </td>
                                        }
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <SmartPagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={(p) => setPage(p)}
                        />
                    </>
                ) : (
                    <p className="text-center text-muted py-4">No IPs found</p>
                )}
            </div>

            {/* Create Modal */}
            <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add IP</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateIP}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>IP Address</Form.Label>
                            <Form.Control
                                value={newIP.ip_address}
                                onChange={(e) =>
                                    setNewIP({ ...newIP, ip_address: e.target.value })
                                }
                                required
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Type</Form.Label>
                            <Form.Select
                                value={newIP.type}
                                onChange={(e) =>
                                    setNewIP({ ...newIP, type: e.target.value })
                                }
                            >
                                <option value="ALLOW">Allow</option>
                                <option value="BLOCK">Block</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreate(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                            Create
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit IP</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateIP}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>IP Address</Form.Label>
                            <Form.Control
                                value={editIP.ip_address}
                                onChange={(e) =>
                                    setEditIP({ ...editIP, ip_address: e.target.value })
                                }
                                required
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Type</Form.Label>
                            <Form.Select
                                value={editIP.type}
                                onChange={(e) =>
                                    setEditIP({ ...editIP, type: e.target.value })
                                }
                            >
                                <option value="ALLOW">Allow</option>
                                <option value="BLOCK">Block</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEdit(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                            Update
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Layout>
    );
}

export default IpManagement;
